/*
 * Copyright (c) 2019.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Environment, HTTP_STATUS} from 'compob2b-libs';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, filter, switchMap, take} from 'rxjs/operators';
import {AuthService} from '../services/auth.service';

/**
 * Globally intercepts requests to backend
 * and adds Authorization header
 * @see {@link https://medium.com/angular-in-depth/top-10-ways-to-use-interceptors-in-angular-db450f8a62d6}
 * @see {@link https://angular-academy.com/angular-jwt/}
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(private readonly auth: AuthService,
              @Inject('environment') private readonly environment: Environment) {
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    /**
     * Get the auth token from the service.
     */
    const authToken = this.auth.getToken();

    /**
     * Clone the request and replace the original headers with
     * cloned headers, updated with the authorization.
     */

    if (authToken) {
      const authReq = this.addToken(req, authToken);

      /**
       * send cloned request with header to the next handler.
       */

      return next
        .handle(authReq)
        .pipe(catchError(error => this.handleError(req, error, next)));
    } else {
      return next.handle(req);
    }
  }

  /**
   * @param request Current request
   * @param token JWT
   */
  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    const ignoreToken = request.headers.get('X-IGNORE-TOKEN') || 'no';
    if (ignoreToken === 'yes') {
      return request.clone({headers: request.headers.delete('X-IGNORE-TOKEN', 'yes')});
    }

    return request.clone({
      headers: request.headers.set(
        'Authorization',
        this.environment.tokenName + ' ' + token
      ),
    });
  }

  /**
   * Handling errors with refreshing request
   * @param request current request
   * @param error given error
   * @param next HttpHandler
   */
  private handleError(
    request: HttpRequest<any>,
    error: HttpErrorResponse,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    console.error(error);

    if (error.headers.get('Compo-Info') === 'BAD_REFRESH' || error.headers.get('Compo-Refresh') === 'BAD_REFRESH') {
      this.auth.logout();
      return throwError('TOKEN ERROR');
    }

    if (
      error instanceof HttpErrorResponse &&
      (error.status === HTTP_STATUS.UNAUTHORIZED || error.status === HTTP_STATUS.FORBIDDEN)
    ) {

      /**
       *  The first request for refreshing token
       */
      if (this.auth.getRefreshing() === false) {

        console.log('POINT 1');
        this.auth.setRefreshing(true);
        this.refreshTokenSubject.next(null);
        return this.auth.refreshToken().pipe(
          switchMap((response) => {
            this.auth.setRefreshing(false);

            if (response instanceof Error) {
              console.error(response);
            } else {
              this.refreshTokenSubject.next(response.data.access.token);
              return next.handle(this.addToken(request, response.data.access.token));
            }
          })
        );
      } else {

        console.log('POINT 2');
        // this.auth.logout();

        /**
         *  If we already made refreshToken request and didn't take a response yet
         *  Waiting for response from condition above
         *  Working this.refreshTokenSubject, auth.refreshToken() instead
         */
        return this.refreshTokenSubject.pipe(
          filter(token => token != null),
          take(1),
          switchMap((token: string) => {
            return next.handle(
              this.addToken(request, token)
            );
          })
        );
      }
    } else {
      console.log('POINT 3');
      return throwError(error);
    }
  }
}
